import { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shuffle, ChevronRight } from 'lucide-react';
import { passTurn } from '../services/circleService';
import { generatePrompt } from '../services/gamePromptService';

export default function PassTurnModal({ isOpen, onClose, circleId, currentCircle, activeGame, currentUserId }) {
  const [passing, setPassing] = useState(false);

  if (!currentCircle || !activeGame) return null;

  const members = Object.entries(currentCircle.memberInfo || {})
    .filter(([uid]) => uid !== currentUserId)
    .map(([uid, info]) => ({ uid, ...info }));

  const handlePass = async (nextUid) => {
    if (passing) return;
    setPassing(true);

    try {
      let newPrompt = null;
      if (activeGame.gameId === 'sketch-guess') {
        newPrompt = await generatePrompt('sketch-guess');
      }

      await passTurn(circleId, nextUid, newPrompt);
      onClose();
    } catch (err) {
      console.error('Failed to pass turn:', err);
    } finally {
      setPassing(false);
    }
  };

  const handleRandom = () => {
    if (members.length === 0) return;
    const pick = members[Math.floor(Math.random() * members.length)];
    handlePass(pick.uid);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <Sheet
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          >
            <SheetHandle />
            <CloseBtn onClick={onClose}><X size={20} /></CloseBtn>

            <Title>Pass to Next Artist</Title>
            <Subtitle>Pick who draws next, or let fate decide</Subtitle>

            <RandomButton onClick={handleRandom} disabled={passing}>
              <Shuffle size={18} />
              Random Roll
            </RandomButton>

            <Divider>or pick someone</Divider>

            <MemberList>
              {members.map((m) => (
                <MemberRow key={m.uid} onClick={() => handlePass(m.uid)} disabled={passing}>
                  <MemberAvatar>
                    {m.photoURL ? (
                      <AvatarImg src={m.photoURL} alt={m.displayName} />
                    ) : (
                      <AvatarFallback>{(m.displayName || '?')[0].toUpperCase()}</AvatarFallback>
                    )}
                  </MemberAvatar>
                  <MemberName>{m.displayName || 'Unknown'}</MemberName>
                  <ChevronRight size={16} />
                </MemberRow>
              ))}
            </MemberList>

            {passing && <PassingLabel>Generating prompt & passing turn...</PassingLabel>}
          </Sheet>
        </>
      )}
    </AnimatePresence>
  );
}

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 200;
`;

const Sheet = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  max-height: 70vh;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.background};
  border-radius: 20px 20px 0 0;
  padding: 16px 24px 32px;
  z-index: 201;
  padding-bottom: max(32px, env(safe-area-inset-bottom));
`;

const SheetHandle = styled.div`
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.border};
  margin: 0 auto 16px;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 8px;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 800;
  text-align: center;
  margin-bottom: 4px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin-bottom: 20px;
`;

const RandomButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  font-size: 17px;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
  transition: all 0.2s;

  &:not(:disabled):hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(245, 158, 11, 0.4);
  }

  &:not(:disabled):active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  margin: 20px 0 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textTertiary};
  display: flex;
  align-items: center;
  gap: 12px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

const MemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MemberRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  width: 100%;
  text-align: left;
  transition: all 0.2s;
  color: ${({ theme }) => theme.colors.textSecondary};

  &:not(:disabled):hover {
    background: ${({ theme }) => theme.colors.surfaceSecondary};
  }

  &:not(:disabled):active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MemberAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  font-size: 16px;
  font-weight: 700;
`;

const MemberName = styled.span`
  flex: 1;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const PassingLabel = styled.div`
  text-align: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.accent};
  margin-top: 16px;
  font-weight: 500;
`;
